import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

// Palette sourced from webapp-corp-handbook (Employee Handbook standalone):
//   --c-accent       #FF6E5B  coral (brand)
//   --c-accent-deep  #E04A36  deep coral
//   --c-bg           #DAD6CD  warm sand
//   --c-bg-deep      #C9C4B8  deeper sand
//   --c-paper        #F1EDE3  cream
//   --c-ink          #1B201F  near-black (green undertone)
//   --c-ink-2        #4A4F4D  secondary ink
//   --c-ink-3        #7A7E7B  tertiary ink
// Dark theme is the handbook's ink as the surface with coral accent + cream
// text (the variant actually used day to day). Light theme is handbook-
// faithful; see the link-contrast note on light primary below.

const HB = {
  accent: '#FF6E5B',
  accentDeep: '#E04A36',
  sand: '#DAD6CD',
  sandDeep: '#C9C4B8',
  paper: '#F1EDE3',
  ink: '#1B201F',
  ink2: '#4A4F4D',
  ink3: '#7A7E7B',
  // Derived dark surfaces (ink lifted a few steps for paper/elevated rows).
  inkPaper: '#262B2A',
  inkNav: '#141817',
};

// Coral header gradient (replaces the purple -> blue wave). Stops sit between
// accentDeep and the AA link coral so the white header text clears WCAG on
// both: title and full-opacity subtitle measure 4.71:1 on the light stop and
// 5.95:1 on the dark stop (the original [accent, accentDeep] left the title
// at 2.75:1 on #FF6E5B).
const HEADER_GRADIENT = ['#CC4530', '#B5371F'];

// The width at which Home's scope band pins the grid's Edit header onto the
// scope row (and the band gains its positioning context). Shared between the
// BackstageContentHeader override below and HomePage's scopeEditBand class so
// the absolute pin and its relative ancestor always flip together — 960 is
// the v4 `md` breakpoint the rest of the app's makeStyles code resolves.
export const SCOPE_EDIT_BAND_MEDIA = '@media (min-width: 960px)';

// Backstage renders the header subtitle at 0.8 opacity, which undoes the
// gradient fix (blended white@0.8 fails 4.5:1); full opacity keeps it AA.
const COMPONENT_OVERRIDES = {
  BackstageHeader: {
    styleOverrides: {
      subtitle: { opacity: 1 },
    },
  },
  // Home's scope band (HomePage.tsx, `.m42-scope-edit-band`) pins the
  // CustomHomepageGrid's Edit / Save / Cancel header onto the scope row's
  // right edge from md up, so the two half-empty rows collapse into one.
  // This lives here as a NAME-based override because production JSS minifies
  // Backstage class names to `jss4-*` (Backstage sets non-Mui sheet names,
  // which the v4 generator only keeps readable in development), so a
  // component-side `[class*="BackstageContentHeader-container"]` selector
  // pins in dev and silently no-ops in prod. The literal marker class keeps
  // every other ContentHeader untouched, and absolute positioning
  // shrink-wraps the header to its buttons so it never overlays the scope
  // trigger. Below md the scope row can wrap; the stacked layout stays.
  // SCOPE_EDIT_BAND_MEDIA (below) is the one source of truth for when the
  // pin fires — a theme token lookup CANNOT couple the two sides here: the
  // component's v4 makeStyles theme resolves breakpoints.up('md') to 960px,
  // while the styleOverrides adapter hands callbacks a v5-breakpoint theme
  // where md is 900px, so deriving from "the md token" on each side drifts
  // by 60px (measured live) and leaves the header absolute with no relative
  // ancestor in the gap.
  BackstageContentHeader: {
    styleOverrides: {
      container: {
        '.m42-scope-edit-band &': {
          [SCOPE_EDIT_BAND_MEDIA]: {
            position: 'absolute',
            top: 0,
            right: 0,
            margin: 0,
            minHeight: 34,
            alignItems: 'center',
          },
        },
      },
    },
  },
};

const pageThemeConst = {
  home: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave }),
  homePage: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave }),
  documentation: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave2 }),
  tool: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.round }),
  service: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave }),
  website: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave }),
  library: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave }),
  other: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave }),
  app: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave }),
  apis: genPageTheme({ colors: HEADER_GRADIENT, shape: shapes.wave }),
};

export const lightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      text: {
        primary: HB.ink,
        secondary: HB.ink2,
        disabled: HB.ink3,
        hint: HB.ink3,
      },
      // AA-darkened coral for body-size link text on the cream surface:
      // #B5371F gives 4.7:1 on #F1EDE3 (vs the brand accentDeep #E04A36 at
      // 3.45:1, which fails AA for body text). The handbook accentDeep is
      // still used for fills/icons/large text where 3:1 is the threshold;
      // this token only changes for primary.main, which Backstage routes
      // through entity links.
      primary: {
        main: '#B5371F',
      },
      secondary: {
        main: HB.accent,
      },
      error: {
        main: '#8c4351',
      },
      warning: {
        main: '#8f5e15',
      },
      info: {
        main: '#34548a',
      },
      success: {
        main: '#485e30',
      },
      background: {
        default: HB.sand,
        paper: HB.paper,
      },
      banner: {
        info: '#34548a',
        error: '#8c4351',
        text: HB.ink,
        link: HB.accentDeep,
      },
      errorBackground: '#8c4351',
      warningBackground: '#8f5e15',
      infoBackground: '#34548a',
      navigation: {
        background: HB.ink,
        indicator: HB.accent,
        color: HB.sand,
        selectedColor: HB.paper,
      },
    },
  }),
  defaultPageTheme: 'home',
  pageTheme: pageThemeConst,
  components: COMPONENT_OVERRIDES,
});

export const darkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      text: {
        primary: HB.paper,
        secondary: HB.sandDeep,
        disabled: HB.ink3,
        hint: HB.sandDeep,
      },
      // #FF6E5B on the ink surface (#1B201F) is 6.16:1 -- clears AA for body
      // text, so coral works as a link color here.
      primary: {
        main: HB.accent,
      },
      secondary: {
        main: HB.accentDeep,
      },
      error: {
        main: '#cd5c5c',
      },
      warning: {
        main: '#c08a3e',
      },
      info: {
        main: '#5b8bc4',
      },
      success: {
        main: '#7a9a5e',
      },
      background: {
        default: HB.ink,
        paper: HB.inkPaper,
      },
      banner: {
        info: '#5b8bc4',
        error: '#cd5c5c',
        text: HB.paper,
        link: HB.accent,
      },
      errorBackground: '#5c2b2e',
      warningBackground: '#8f5e15',
      infoBackground: '#34548a',
      navigation: {
        background: HB.inkNav,
        indicator: HB.accent,
        color: HB.sandDeep,
        selectedColor: HB.accent,
      },
    },
  }),
  defaultPageTheme: 'home',
  pageTheme: pageThemeConst,
  components: COMPONENT_OVERRIDES,
});
